import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import { concat, EMPTY, Observable, of } from 'rxjs';
import { from } from 'rxjs/internal/observable/from';
import { HttpService } from '../../global/http.service';
import { downloadFile } from '../../global/fileDownload';
import * as reports from './reports.actions';

import {
  ReportsDownloadRequestPayload, ReportsAvailabilityRequest,
  ReportsAvailability, ReportsExcelDownloadRequestPayload
} from './reports.models';
import { DatesInterval, DatesIntervalRequest } from '../../global/interfaces';
import { Actions as ActionsCDK } from "atlas-cdk";
import { ToastService } from '../../shared/toaster_/toaster_.service';

@Injectable()
export class ReportsEffects {
  public loadReportsDates$ = createEffect(() => this.actions$.pipe(
    ofType<reports.LoadReportsDatesAction>(reports.ActionTypes.LOAD_REPORTS_DATES),
    map(action => action.payload),
    switchMap(payload =>
      this.loadReportsDates(payload).pipe(
        map((response: DatesInterval) =>
          new reports.LoadReportsDatesSuccessAction(response)
        ),
        catchError(error =>
          //   from([
          //   new reports.LoadReportsDatesFailedAction(error),
          //   new AddErrorToastAction(error.error.message)
          // ])
          // {
          //   this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
          //   return of(new reports.LoadReportsDatesFailedAction(error));
          // }
          concat(
            of(new reports.LoadReportsDatesFailedAction(error)),
            of(null).pipe(
              tap(() => {
                this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
              })
            )
          )
        )
      )
    )
  ));

  public loadReportsAvailability$ = createEffect(() => this.actions$.pipe(
    ofType<reports.LoadReportsAvailabilityAction>(reports.ActionTypes.LOAD_REPORTS_AVAILABILITY),
    map(action => action.payload),
    switchMap(payload =>
      this.loadReportsAvailability(payload).pipe(
        map(response =>
          new reports.LoadReportsAvailabilitySuccessAction(response)
        ),
        catchError(error => from([
          new reports.LoadReportsAvailabilityFailedAction(error)
        ]))
      )
    )
  ));

  public reportsDownload$ = createEffect(() => this.actions$.pipe(
    ofType<reports.ReportsDownloadAction>(reports.ActionTypes.REPORTS_DOWNLOAD),
    map(action => action.payload),
    switchMap(payload =>
      this.downloadReport(payload).pipe(
        map((response: any) => {
          // deepcode ignore DOMXSS:internally created secure response
          downloadFile(response.body, 'application/pdf', 'MonthlyReport.pdf');

        }),
        catchError(() => {
          // from([
          //   new AddErrorToastAction('Download failed. Please try again later.')
          // ])

          this._toasterService.launchAlertError('Download failed. Please try again later.', ActionsCDK.error);
          return EMPTY;
        }
        )
      )
    )
  ), { dispatch: false });

  public fileExcelDownload$ = createEffect(() => this.actions$.pipe(
    ofType<reports.ReportsExcelDownloadAction>(reports.ActionTypes.REPORTS_EXCEL_DOWNLOAD),
    map(action => action.payload),
    switchMap(payload =>
      this.downloadExcelReportFile(payload).pipe(
        map((response: any) => {
          const fileName = (`${payload.type}` === 'investment-summary') ? 'InvestmentSummary.csv' : 'CombinedAccountView.csv';
          // deepcode ignore DOMXSS:internally created secure response
          downloadFile(response.body, 'text/csv', fileName);
        }),
        catchError(() => {
          // from([
          //   new AddErrorToastAction('Download failed. Please try again later.')
          // ])

          this._toasterService.launchAlertError('Download failed. Please try again later.', ActionsCDK.error);
          return EMPTY;
        }
        )
      )
    )
  ), { dispatch: false });

  public constructor(
    private readonly actions$: Actions,
    private readonly http: HttpService,
    private readonly _toasterService: ToastService
  ) {
  }

  /**
   * @TODO Replace performance dates url with one intended for reports
   */
  private loadReportsDates(payload: DatesIntervalRequest): Observable<DatesInterval> {
    return this.http.get(`/metrics/${payload.account}/date-ranges/report`);
  }

  private loadReportsAvailability(payload: ReportsAvailabilityRequest): Observable<ReportsAvailability[]> {
    return this.http.post(`/reports/${payload.account}/availability`, { date: payload.date });
  }

  private downloadReport(payload: ReportsDownloadRequestPayload): Observable<Blob> {
    return this.http.post(`/reports/${payload.account}`, {
      date: payload.date, type: payload.type,
      country: payload.country
    }, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  private downloadExcelReportFile(payload: ReportsExcelDownloadRequestPayload): Observable<any> {
    return this.http.post(`/reports/${payload.type}/export`, payload, {
      observe: 'response',
      responseType: 'blob',
    });
  }
}
